import cn from 'classnames';
import qs from 'query-string';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { EDDY_URL } from 'config';
import { tokenSelector } from 'redux/modules/auth';
import bem from 'utils/bem';
import { EddyContext, EddyElement, EddyEmbedProps } from './types';
import useApplicationReadyMessaging from './useApplicationReadyMessaging';
import useClipHighlightMessaging from './useClipHighlightMessaging';
import useClipSelectMessaging from './useClipSelectMessaging';
import { useContextMenuMessaging } from './useContextMenuMessaging';
import usePauseMessaging from './usePauseMessaging';
import usePlayMessaging from './usePlayMessaging';
import { useResetMessaging } from './useResetMessaging';
import useSeekMessaging from './useSeekMessaging';
import useTimeUpdateMessaging from './useTimeUpdateMessaging';
import useTranscriptUpdateMessaging from './useTranscriptUpdateMessaging';

const block = bem('eddy-embed');

type CreateIframeUrlProps = {
  accessToken?: string;
  context?: EddyContext;
  endMillis?: number;
  isAudioEnabled?: boolean;
  margin?: string;
  projectId: string;
  startMillis?: number;
};

function createIframeUrl({
  projectId,
  accessToken,
  context,
  startMillis,
  endMillis,
  margin,
  isAudioEnabled,
}: CreateIframeUrlProps) {
  const url = `${EDDY_URL}/transcript/${projectId}/embed`;

  return qs.stringifyUrl({
    url,
    query: {
      accessToken,
      context,
      startMillis,
      endMillis,
      margin,
      audioEnabled: isAudioEnabled,
    },
  });
}

const EddyEmbed = React.forwardRef<EddyElement, EddyEmbedProps>(
  (
    {
      className,
      eddyProjectId,
      onClipSelect,
      onPause,
      onPlay,
      onTimeUpdate,
      onTranscriptUpdate,
      onApplicationReady,
      onSeek,
      context,
      margin,
      startMillis,
      endMillis,
      isAudioEnabled,
      ...props
    },
    ref,
  ) => {
    const token = useSelector(tokenSelector);
    const [iframe, setIframe] = useState<HTMLIFrameElement | null>(null);
    const isPlaying = useRef(false);

    useTranscriptUpdateMessaging({ iframe, onTranscriptUpdate });
    useTimeUpdateMessaging({ iframe, onTimeUpdate });
    useClipSelectMessaging({ iframe, onClipSelect });
    useApplicationReadyMessaging({ iframe, onApplicationReady });

    const handlePlay = useCallback(() => {
      isPlaying.current = true;
      onPlay?.();
    }, [onPlay]);

    const handlePause = useCallback(() => {
      isPlaying.current = false;
      onPause?.();
    }, [onPause]);

    const { highlightClip } = useClipHighlightMessaging({
      iframe,
    });

    const { pause } = usePauseMessaging({ iframe, onPause: handlePause });
    const { play } = usePlayMessaging({ iframe, onPlay: handlePlay });
    const { reset } = useResetMessaging({ iframe });
    const { seek } = useSeekMessaging({ iframe, onSeek });
    const { closeContextMenu } = useContextMenuMessaging({ iframe });

    const handleIsPlaying = useCallback(() => {
      return isPlaying.current;
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        focusClip: highlightClip,
        pause,
        play,
        reset,
        seek,
        isPlaying: handleIsPlaying,
      }),
      [highlightClip, pause, play, reset, seek, handleIsPlaying],
    );

    useEffect(() => {
      const handlePointerDown = (e: PointerEvent) => {
        if (!iframe?.contains(e.target as Node)) {
          closeContextMenu();
        }
      };

      document.addEventListener('pointerdown', handlePointerDown);

      return () => {
        document.removeEventListener('pointerdown', handlePointerDown);
      };
    }, [closeContextMenu, iframe]);

    return (
      <iframe
        className={cn(block(), className)}
        ref={setIframe}
        src={createIframeUrl({
          projectId: eddyProjectId,
          accessToken: token,
          context,
          startMillis,
          endMillis,
          margin,
          isAudioEnabled,
        })}
        {...props}
      />
    );
  },
);

export default EddyEmbed;
